import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'; // <-- ADDED
import { useAudioPlayer } from '@/museum-app/context/audio-player-context'; // Import Audio Player Hook
import { useSettings } from '@/museum-app/context/settings-context';
import { Globe, Pause as PauseIcon, Play, Volume2 } from 'lucide-react'; // Import necessary icons, added PauseIcon, Volume2
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { BackButton } from '../../components/molecules/back-button';
import RelatedExhibits from '../../features/rooms/components/related-exhibits';
import RoomInfo from '../../features/rooms/components/room-info';
import { useRooms } from '../../hooks/use-rooms';
import { ExhibitMediaItem, Room } from '../../types/models'; // Remove RoomMediaItem, keep ExhibitMediaItem

// Dynamically import heavy media components
const LazyRoomGallery = lazy(() => import('../../features/rooms/components/room-gallery').then((module) => ({ default: module.RoomGallery }))); // Named export
const LazyPanoramaViewer = lazy(() =>
    import('../../components/media/panorama/panorama-viewer').then((module) => ({ default: module.PanoramaViewer })),
);
// Keep LazyAudioPlayer for detailed view when active
const LazyAudioPlayer = lazy(() => import('../../components/media/audio/audio-player').then((module) => ({ default: module.AudioPlayer })));

// Import specific skeleton components
// Removed AudioPlayerSkeleton import
import { RoomGallerySkeleton } from '../../features/rooms/components/room-gallery-skeleton';
// Import the shared placeholder component
import { MediaLoadingPlaceholder } from '../../components/molecules/media-loading-placeholder';

/**
 * Room detail page component
 */
const RoomDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { fetchRoomById } = useRooms();
    const { t } = useTranslation();
    const [room, setRoom] = useState<Room | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const { language } = useSettings();
    const { loadAudio, currentMedia, isPlaying, pause: pauseGlobalAudio } = useAudioPlayer(); // Get audio context

    // State for lazy loading sections
    const [showGallery, setShowGallery] = useState(false);
    const [showPanorama, setShowPanorama] = useState(false);
    const [showAudio, setShowAudio] = useState(false); // <-- ADDED

    useEffect(() => {
        const loadRoom = async () => {
            if (!id) {
                setError(new Error(t('rooms.errorIdRequired', 'Room ID is required')));
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const roomData = await fetchRoomById(parseInt(id, 10));

                if (!roomData) {
                    setError(new Error(t('rooms.errorNotFound', 'Room not found')));
                } else {
                    setRoom(roomData);
                    setError(null);
                }
            } catch (err) {
                setError(err instanceof Error ? err : new Error(t('rooms.errorFailedToLoad', 'Failed to load room')));
                console.error('Error loading room:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadRoom();
        // Reset show states when ID changes
        setShowGallery(false);
        setShowPanorama(false);
        setShowAudio(false); // <-- ADDED
    }, [id, fetchRoomById, t]);

    // Helper function specifically for creating ExhibitMediaItem for Audio Context/Player
    const createExhibitMediaItemForRoomAudio = (url: string, type: string, baseTitle: string): ExhibitMediaItem => {
        const currentRoomTranslation = room?.translations?.find((t) => t.language_code === language);
        const title = currentRoomTranslation?.name || baseTitle;
        const description = currentRoomTranslation?.description || null;
        const filePath = url.startsWith('http') ? url : '/storage/' + url; // Calculate file path once

        // Map RoomTranslations to ExhibitMediaTranslation structure
        const exhibitMediaTranslations =
            room?.translations?.map((t) => ({
                language_code: t.language_code,
                title: t.name, // Map name to title
                description: t.description,
                alt_text: t.name, // Use name for alt text
                caption: t.description, // Use description for caption
                file_path: filePath, // Add file_path here as well
            })) || [];

        return {
            id: room?.id || 0, // Using room ID as placeholder for media ID
            media_type: type,
            file_path: filePath, // Use calculated file path
            title: title,
            description: description,
            order_position: 0, // Placeholder
            translations: exhibitMediaTranslations, // Use the mapped translations
        };
    };

    // Check if media is available
    const hasPhotos = room?.gallery_urls && room.gallery_urls.length > 0;
    const hasPanoramas = !!room?.panorama_url;
    const hasAudio = !!room?.audio_guide_url;

    // Compute media tabs and default tab
    const mediaTabs = [
        { key: 'gallery', condition: hasPhotos },
        { key: 'panorama', condition: hasPanoramas },
    ];
    const defaultTab = mediaTabs.find((tab) => tab.condition)?.key || '';

    // Get current translation and base title
    const currentTranslation = room?.translations.find((tr) => tr.language_code === language) || room?.translations[0];
    const roomBaseTitle = currentTranslation?.name || t('rooms.untitledRoom', 'Untitled Room');

    // Create media objects using the correct helper and null checks
    // Explicitly type audioMediaObject
    const audioMediaObject: ExhibitMediaItem | null =
        hasAudio && room?.audio_guide_url ? createExhibitMediaItemForRoomAudio(room.audio_guide_url, 'audio', roomBaseTitle) : null;
    // Renamed helper function to return ExhibitMediaItem for Gallery/Panorama as well
    const createExhibitMediaItemForRoomMedia = (url: string, type: string, baseTitle: string): ExhibitMediaItem => {
        const currentRoomTranslation = room?.translations?.find((t) => t.language_code === language);
        const title = currentRoomTranslation?.name || baseTitle;
        const description = currentRoomTranslation?.description || null;
        const filePath = url.startsWith('http') ? url : '/storage/' + url;

        // Map RoomTranslations to ExhibitMediaTranslation structure
        const exhibitMediaTranslations =
            room?.translations?.map((t) => ({
                language_code: t.language_code,
                title: t.name,
                description: t.description,
                alt_text: t.name,
                caption: t.description,
                file_path: filePath, // Include file_path
            })) || [];

        return {
            id: room?.id || 0, // Using room ID as placeholder
            media_type: type,
            file_path: filePath,
            title: title,
            description: description,
            order_position: 0, // Placeholder
            translations: exhibitMediaTranslations,
        };
    };
    // Update calls to use the new helper function
    const panoramaMediaObject =
        hasPanoramas && room?.panorama_url ? createExhibitMediaItemForRoomMedia(room.panorama_url, 'panorama', roomBaseTitle) : null;
    const galleryMediaItems =
        hasPhotos && room?.gallery_urls ? room.gallery_urls.map((url) => createExhibitMediaItemForRoomMedia(url, 'image', roomBaseTitle)) : [];

    const isCurrentAudioPlaying = currentMedia?.id === room?.id && currentMedia?.media_type === 'audio';

    if (isLoading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex items-center justify-center py-12">
                    <Skeleton className="h-12 w-12 rounded-full" />
                </div>
            </div>
        );
    }

    if (error || !room) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                    <p className="font-medium">{t('rooms.errorLoadingTitle', 'Error loading room')}</p>
                    <p className="mt-1 text-sm">{error?.message || t('rooms.errorNotFound', 'Room not found')}</p>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => navigate('/rooms')} // Use navigate directly
                        className="mt-2"
                    >
                        {t('common.returnToRoomList', 'Return to Room List')}
                    </Button>
                </div>
            </div>
        );
    }

    console.log('Room data:', room);

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Use BackButton component */}
            <BackButton to="/rooms" text={t('common.backToRooms', 'Back to Rooms')} ariaLabel={t('common.backToRoomsAriaLabel', 'Back to rooms')} />

            {/* Room content */}
            <div className="flex flex-col gap-8">
                {/* Media Tabs Section without Audio */}
                {(hasPhotos || hasPanoramas) && (
                    <Tabs defaultValue={defaultTab} className="mb-8 w-full">
                        <TabsList className="mb-4 grid w-full grid-cols-2">
                            {hasPhotos && (
                                <TabsTrigger value="gallery" onClick={() => setShowGallery(true)}>
                                    {t('common.gallery', 'Gallery')}
                                </TabsTrigger>
                            )}
                            {hasPanoramas && (
                                <TabsTrigger value="panorama" onClick={() => setShowPanorama(true)}>
                                    {t('common.panorama', '360° View')}
                                </TabsTrigger>
                            )}
                        </TabsList>
                        {hasPhotos && (
                            <TabsContent value="gallery">
                                {!showGallery ? (
                                    <RoomGallerySkeleton onClick={() => setShowGallery(true)} />
                                ) : (
                                    <Suspense fallback={<RoomGallerySkeleton />}>
                                        <LazyRoomGallery media={galleryMediaItems as any} languageCode={language} />
                                    </Suspense>
                                )}
                            </TabsContent>
                        )}
                        {hasPanoramas && (
                            <TabsContent value="panorama">
                                {!showPanorama ? (
                                    <MediaLoadingPlaceholder icon={Globe}>
                                        <Button variant="secondary" size="sm" onClick={() => setShowPanorama(true)} className="mt-4">
                                            <Play className="mr-2 h-4 w-4" />
                                            {t('common.loadPanorama', 'Load 360° View')}
                                        </Button>
                                    </MediaLoadingPlaceholder>
                                ) : (
                                    <Suspense fallback={<MediaLoadingPlaceholder icon={Globe} />}>
                                        <div className="h-[400px] w-full overflow-hidden rounded-lg border dark:border-neutral-800">
                                            {panoramaMediaObject && (
                                                <LazyPanoramaViewer media={panoramaMediaObject as any} showControls={true} showMinimap={false} />
                                            )}
                                        </div>
                                    </Suspense>
                                )}
                            </TabsContent>
                        )}
                    </Tabs>
                )}

                {/* New Audio Guide Section placed above RoomInfo */}
                {hasAudio && (
                    <div className="mb-8">
                        <h2 className="mb-4 flex items-center text-xl font-semibold">
                            <Volume2 className="mr-2 h-5 w-5" />
                            {t('common.audioGuide', 'Audio Guide')}
                        </h2>
                        {audioMediaObject && (
                            <div className="space-y-4">
                                <Button
                                    variant={isCurrentAudioPlaying ? 'outline' : 'default'}
                                    onClick={() => {
                                        if (isCurrentAudioPlaying && isPlaying) {
                                            pauseGlobalAudio();
                                        } else {
                                            loadAudio(audioMediaObject, true);
                                        }
                                    }}
                                    className="w-full"
                                >
                                    {isCurrentAudioPlaying && isPlaying ? <PauseIcon className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />}
                                    {isCurrentAudioPlaying && isPlaying
                                        ? t('common.pauseAudio', 'Pause Audio')
                                        : t('common.playAudio', 'Play Audio Guide')}
                                </Button>
                                {/* {isCurrentAudioPlaying && audioMediaObject && (
                                    <Suspense fallback={<Skeleton className="h-48 w-full rounded-lg" />}>
                                        <LazyAudioPlayer media={audioMediaObject} showTranscript={true} className="mt-4" />
                                    </Suspense>
                                )} */}
                            </div>
                        )}
                    </div>
                )}

                {/* Room information */}
                <RoomInfo languageCode={language} room={room} />

                {/* Related exhibits */}
                {room.exhibits && room.exhibits.length > 0 && (
                    <div className="border-t border-gray-200 pt-8 dark:border-gray-700">
                        <RelatedExhibits exhibits={room.exhibits} roomId={room.id} languageCode={language} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default RoomDetail;
